import React, {useMemo} from 'react';
import {htmlDecode} from "../../../utils/htmlDecode";
import {chakra, Flex, Table, Tbody, Td, Text, Th, Thead, Tr} from '@chakra-ui/react'
import {TriangleDownIcon, TriangleUpIcon} from '@chakra-ui/icons'
import {useSortBy, useTable} from 'react-table'
import {useNavigate, Link, useSearchParams} from "react-router-dom";

const mapping = [
    {title: 'Year Funded', accessor: 'first_funded_year'},
    {title: 'Company', accessor: 'company_name'},
    {title: 'Description', accessor: 'description'},
    {title: 'Company Status', accessor: 'status'},
    {title: 'Sector', accessor: 'sectors'},
    {title: 'First Investment', accessor: 'first_funded_round'},
]

const headers = [
    {children: 'Year Funded', maxW: 90, display: ['none', 'table-cell']},
    {
        children: 'Company',
        maxW: 180,
        fontSize: 'lg',
        lineHeight: 'normal',
        fontWeight: 'semibold',
        textColor: '#333333',
        fontFamily: 'MarkWebMedium'
    },
    {
        children: 'Description',
        maxW: 400,
        display: ['none', 'table-cell'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    {children: 'Company Status', minW: 115, maxW: 115, display: ['none', 'table-cell']},
    {children: 'Sector', maxW: 150, display: ['none', 'table-cell']},
    {children: 'First Investment', minW: 130, maxW: 130, display: ['none', 'table-cell']},
];

export const SortableTable = ({companies}) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeSector = searchParams.get('sector');
    const showAll = searchParams.get('show');
    
    const data = useMemo(
        () => companies.map((company) => {
            // console.log('sortTable 1', window.performance.memory.usedJSHeapSize);
        
            return {
                ...company,
                description: company.long_description,
                status: company?.investment_status?.length > 0
                    ? company.investment_status[0].name
                    : '',
                sectors: company?.investment_sectors?.length > 0
                    ? company?.investment_sectors.map((i) => htmlDecode(i.name)).join(', ')
                    : '',
            }
        }),
        [companies],
    )


    const numberSort = useMemo(
        () => (a, b) => {
            // console.log('sortTable 2', window.performance.memory.usedJSHeapSize);
            return /[A-Za-z]/.test(a?.original?.company_name) - /[A-Za-z]/.test(b?.original?.company_name) || (a?.original?.company_name?.toUpperCase() < b?.original?.company_name?.toUpperCase() ? -1 : a?.original?.company_name?.toUpperCase() > b.original.company_name?.toUpperCase() ? 1 : 0)
        },
        []
    );

    const regularSort = useMemo(() => (rowA, rowB, columnId) => {
        // console.log('sortTable 3', window.performance.memory.usedJSHeapSize);

        const a = rowA.original[columnId] ? rowA.original[columnId].toLowerCase() : '';
        
        const b = rowB.original[columnId] ? rowB.original[columnId].toLowerCase() : '';
        
        if (a > b) 
            return 1
        
        if (b > a) 
            return -1
        
        return 0
    })

    const sortTypes = {
        numberSort, regularSort
    };

    const columns = useMemo(
        () => headers.map((header) => {
            // console.log('sortTable 4', window.performance.memory.usedJSHeapSize);
            return {
                Header: header.children,
                accessor: mapping.find((key) => key.title === header.children).accessor,
                maxW: header.maxW,
                fontWeight: header.fontWeight,
                fontFamily: header.fontFamily,
                fontSize: header.fontSize,
                textColor: header.textColor,
                display: header.display,
                lineHeight: header.lineHeight,
                sortType: header.children === 'Company' ? numberSort : regularSort
            }
        }),
        [numberSort],
    )


    const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} =
        useTable({
            columns, data, sortTypes, 
            disableSortRemove: true, enableSortingRemoval: false, initialState: {
                sortBy: [
                    {
                        id: 'company_name',
                        desc: false,
                    }
                ]
            }
        }, useSortBy)

    return (
        <Table {...getTableProps()}>
            <Thead border={'none'}>
                {headerGroups.map((headerGroup) => (
                    <Tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => {
                            return (
                                <Th
                                    {...column.getHeaderProps({
                                        ...column.getSortByToggleProps(),
                                        style: {maxWidth: column.maxW, width: column.maxW},
                                    })}
                                    isNumeric={column.isNumeric}
                                    borderColor={'brandAlpha.200'}
                                    textColor={'brand.700'}
                                    maxW={column.maxW}
                                    minW={column.minW}
                                    w={column.maxW}
                                    display={column.display}
                                >
                                    <Flex alignItems="center" whiteSpace={'nowrap'}>
                                        {column.render('Header')}
                                        <chakra.span pl='1'>
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <TriangleDownIcon aria-label='sorted descending'/>
                                                ) : (
                                                    <TriangleUpIcon aria-label='sorted ascending'/>
                                                )
                                            ) : (
                                                column.isSortedDesc ? (
                                                    <TriangleDownIcon aria-label='descending' color={'lightgray'}/>
                                                ) : (
                                                    <TriangleUpIcon aria-label='ascending' color={'lightgray'}/>
                                                )
                                            )}
                                        </chakra.span>
                                    </Flex>
                                </Th>
                            )
                        })}
                    </Tr>
                ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
                {rows.map((row, rowIndex) => {
                    prepareRow(row)
                    /*
                        onClick={() => {
                            navigate(`/companies/${row.original.slug}`);
                        }}

                    as={Link}
                to={`/companies/${slug}${(activeSector?`?sector=${activeSector}`:``)}`}
                    */
                    return (
                        <Tr {...row.getRowProps()} cursor="pointer" onClick={() => {
                            navigate(`/companies/${row.original.slug}${(activeSector ? `?sector=${activeSector}` : '')}${(showAll ? `?show=all` : '')}`);
                        }}>
                            {row.cells.map((cell, cellIndex) => {
                                // console.log('rendered cell', cell.render('Cell').props.cell.value);
                                return (
                                    <Td {...cell.getCellProps({
                                        style: {
                                            maxWidth: cell.column.maxW,
                                            width: cell.column.maxW,
                                            fontFamily: cell.column.fontFamily,
                                            display: cell.column.display
                                        },
                                        key: `portfolio-td-${rowIndex}-${cellIndex}`
                                    })} display={cell.column.display} isNumeric={cell.column.isNumeric}
                                        maxW={cell.column.maxW} w={cell.column.maxW}>
                                        <Text fontSize={cell.column?.fontSize || 'sm'}
                                              fontFamily={cell.column.fontFamily}
                                              key={`portfolio-text-${rowIndex}-${cellIndex}`}
                                              fontWeight={cell.column.fontWeight}
                                              lineHeight={cell.column.lineHeight || '3'}
                                              noOfLines={1}
                                              textColor={'#333333'}>{(cell.render('Cell')?.props?.cell?.value == 'Series B' ? 'Series B+' : cell.render('Cell'))}</Text>
                                    </Td>
                                )
                            })}
                        </Tr>
                    )
                })}
            </Tbody>
        </Table>
    )
}